import { useCallback, useEffect, useMemo, useState } from 'react';

import useApi from '@/hooks/useApi';

import { TTab } from '@/components/global/Tabs/Tab';

import { TRecommendation } from '@/services/requests/Recommendation/types/template1';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import {
  TDisplayedService,
  TInteractionTabClick,
} from '@/services/requests/Interactions/types';

import { removeAccents } from '@/helpers/StringHelper/removeAccents';
import { getValue } from '@/helpers/GetValueHelper';

import { saveOption, getOption } from './utils';

const useTabs = (data: TRecommendation[]) => {
  const { Api } = useApi();

  const getTabItemId = (item: TRecommendation) => {
    return `tab-${item.idLiferay}`;
  };

  const [idsViewed, setIdsViewed] = useState<string[]>(() => [data[0].id]);
  const [tabItems, setTabItems] = useState<TTab[]>(() => {
    return data.map((item, index) => ({
      id: getTabItemId(item),
      key: getValue(item, 'header.key', `key-${item.idLiferay}`) as string,
      label: item.header.prefix,
      viewed: false,
      active: index === 0,
      recommendationId: item.id,
    }));
  });

  const firstTab = tabItems[0].id;

  const getTabName = (tab: TRecommendation) => {
    return tab.header.prefix || '';
  };

  const getTabActive = (item: TRecommendation) => {
    return tabItems.find(value => value.id === getTabItemId(item))?.active;
  };

  const getOptionActiveInitially = (item: TRecommendation) => {
    return item.config.enable_save_variant?.toString() === 'true'
      ? `tab-educ_${item.config.cd_variant?.toLocaleLowerCase()}`
      : null;
  };

  const hasTabs = useMemo(() => {
    return data[0].layout.toString() !== '5';
  }, [data]);

  const hasOptions = useMemo(() => {
    return !hasTabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTabItemToActive = useCallback(
    (tabId: string, click?: boolean) => {
      setTabItems(prev => {
        return prev.map(item => {
          if (item.id === tabId) {
            const tabNameFormatted = removeAccents(
              item.key.toLocaleLowerCase(),
            );
            if (click) {
              dispatchInteraction<TInteractionTabClick>(Api, 'tab-click', {
                recommendationId: item.recommendationId,
                tabName: tabNameFormatted,
              });
            }

            if (!idsViewed.includes(item.recommendationId)) {
              setIdsViewed(ids => [...ids, item.recommendationId]);
              dispatchInteraction<TDisplayedService>(Api, 'displayed', {
                recommendationId: item.recommendationId,
              });
            }

            return { ...item, active: true };
          }

          return { ...item, active: false };
        });
      });
    },
    [Api, idsViewed],
  );

  const handleOptionClick = useCallback((tabId: string) => {
    saveOption(data[0].type, tabId);
    updateTabItemToActive(tabId, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = useCallback((tabId: string) => {
    updateTabItemToActive(tabId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasOptions) {
      const optionStored =
        getOption(data[0].type) || getOptionActiveInitially(data[0]);

      if (optionStored) {
        updateTabItemToActive(optionStored);
      }
    }
  }, [data, hasOptions, firstTab, updateTabItemToActive]);

  return {
    tabItems,
    hasOptions,
    hasTabs,
    getTabActive,
    getTabName,
    onTabClick: handleTabClick,
    onOptionClick: handleOptionClick,
  };
};

export default useTabs;
